<script setup lang="ts">
import ColorModeToggle from '@/Components/ColorModeToggle.vue';
import useColorMode from '@/Composables/useColorMode';
import { useFeatures } from '@/Composables/useFeatures';
import useTheme from '@/Composables/useTheme';
import { AppSetting } from '@/Interfaces/models';
import type { PageProps } from '@inertiajs/core';
import { Head, usePage } from '@inertiajs/vue3';
import { computed, ref } from 'vue';
import defaultLogoMobile from './../../../images/1Brain-logo-no-tagline.svg';
import darkLogoMobile from './../../../images/1brain_dark_mode_logo.svg';

interface MyPageProps extends PageProps {
  app_settings: AppSetting;
}
const page = usePage<MyPageProps>();
const { isEnabled } = useFeatures();
const { mode } = useColorMode();

interface SubscriptionPlan {
  name: string;
  max_seats: string;
  monthly_pricing: string;
  monthly_pricing_annual: string;
  yearly_pricing: string;
  icon: string;
  custom: boolean;
}

interface DisplayPlan {
  title: string;
  description: string;
  icon: string;
  price?: string;
  custom?: boolean;
}

interface Props {
  title: string;
  subtitle: string;
  description?: string;
}
const props = defineProps<Props>();

const isDale = computed(() => page.props.is_dale);

const priceBy = ref<'monthly' | 'yearly'>('monthly');

const subscriptionPlans = computed(() => {
  const subscriptionPlans = page.props.subscription_plans as SubscriptionPlan[];

  const maxSeatsValue = Math.max(
    ...subscriptionPlans.map((plan) => parseInt(plan.max_seats, 10)),
  );

  const plans: DisplayPlan[] = subscriptionPlans.map((plan) => ({
    title: plan.name,
    description: `${plan.max_seats} users`,
    price: `$${priceBy.value === 'monthly' ? plan.monthly_pricing : plan.monthly_pricing_annual}`,
    icon: plan.icon,
  }));

  return [
    ...plans,
    {
      title: 'Custom',
      description: `+${maxSeatsValue} users`,
      custom: true,
      icon: 'city',
    },
  ];
});

const pricingOptions = [
  {
    title: 'Micro',
    description: '1-5 employees',
    price: '$19',
    icon: 'house-window',
  },
  {
    title: 'Small',
    description: '6-20 employees',
    price: '$49',
    icon: 'store',
  },
  {
    title: 'Growth',
    description: '21-50 employees',
    price: '$99',
    icon: 'building',
  },
  {
    title: 'Custom',
    description: '51-500 employees',
    custom: true,
    icon: 'city',
  },
];

const links = [
  {
    label: 'Home',
    href: 'https://1brain.it/',
  },
  {
    label: 'Demo',
    href: 'https://1brain.it/demo/',
  },
  {
    label: 'Pricing',
    href: 'https://1brain.it/pricing/',
  },
  {
    label: 'FAQs',
    href: 'https://1brain.it/faqs/',
  },
  {
    label: 'Blog',
    href: 'https://1brain.it/blog/',
  },
  {
    label: 'Support',
    href: 'https://support.1brain.it/en/',
  },
];

const socials = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com',
    icon: `
    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="24" height="24" viewBox="0 0 50 50">
        <path d="M25,3C12.85,3,3,12.85,3,25c0,11.03,8.125,20.137,18.712,21.728V30.831h-5.443v-5.783h5.443v-3.848 c0-6.371,3.104-9.168,8.399-9.168c2.536,0,3.877,0.188,4.512,0.274v5.048h-3.612c-2.248,0-3.033,2.131-3.033,4.533v3.161h6.588 l-0.894,5.783h-5.694v15.944C38.716,45.318,47,36.137,47,25C47,12.85,37.15,3,25,3z" fill="currentColor"></path>
    </svg>
    `,
  },
  {
    label: 'X',
    href: 'https://x.com',
    icon: `
    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="24" height="24" viewBox="0 0 50 50">
      <path d="M 11 4 C 7.134 4 4 7.134 4 11 L 4 39 C 4 42.866 7.134 46 11 46 L 39 46 C 42.866 46 46 42.866 46 39 L 46 11 C 46 7.134 42.866 4 39 4 L 11 4 z M 13.085938 13 L 21.023438 13 L 26.660156 21.009766 L 33.5 13 L 36 13 L 27.789062 22.613281 L 37.914062 37 L 29.978516 37 L 23.4375 27.707031 L 15.5 37 L 13 37 L 22.308594 26.103516 L 13.085938 13 z M 16.914062 15 L 31.021484 35 L 34.085938 35 L 19.978516 15 L 16.914062 15 z" fill="currentColor"></path>
    </svg>
    `,
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/',
    icon: `
    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="24" height="24" viewBox="0 0 50 50">
        <path d="M41,4H9C6.24,4,4,6.24,4,9v32c0,2.76,2.24,5,5,5h32c2.76,0,5-2.24,5-5V9C46,6.24,43.76,4,41,4z M17,20v19h-6V20H17z M11,14.47c0-1.4,1.2-2.47,3-2.47s2.93,1.07,3,2.47c0,1.4-1.12,2.53-3,2.53C12.2,17,11,15.87,11,14.47z M39,39h-6c0,0,0-9.26,0-10 c0-2-1-4-3.5-4.04h-0.08C27,24.96,26,27.02,26,29c0,0.91,0,10,0,10h-6V20h6v2.56c0,0,1.93-2.56,5.81-2.56 c3.97,0,7.19,2.73,7.19,8.26V39z" fill="currentColor"></path>
    </svg>
    `,
  },
];

const appName = computed(() => page.props.app_settings?.name ?? '1Brain');

const mobileLogo = computed(() => {
  if (mode.value === 'dark') {
    return darkLogoMobile;
  }

  return page.props.app_settings?.mobile_logo ?? defaultLogoMobile;
});

useTheme().load();
</script>

<template>
  <Head :title="`${props.title} - ${appName}`" />
  <div
    class="fixed grid h-screen max-h-screen w-full grid-cols-1 lg:grid-cols-2"
  >
    <!-- <div
      class="my-auto hidden min-h-full w-full items-center justify-center overflow-clip bg-blue-500 bg-cover bg-no-repeat px-5 py-8 lg:flex"
      :style="{ backgroundImage: `url('${background}')` }"
    ></div> -->

    <div></div>
  </div>
  <div class="relative grid max-h-screen grid-cols-1 lg:grid-cols-2">
    <div
      class="relative hidden h-screen w-full overflow-hidden md:items-center md:justify-center lg:flex"
    >
      <div
        class="fixed inset-0 bottom-0 left-0 right-[50%] top-0 m-auto flex flex-col items-center bg-[#e2edef] pt-[4vw] xl:pt-[3.2vw] 2xl:pt-[2vw]"
      >
        <a
          href="https://1brain.it/"
          class="brightness-100 drop-shadow-none transition-all duration-300 hover:brightness-125 hover:drop-shadow-md"
        >
          <img
            src="../../../images/1Brain-logo-no-tagline.svg"
            class="mb-[2vw] block w-full max-w-[16vw] xl:max-w-[22.6vh] 2xl:max-w-[12vw]"
            alt="logo"
          />
        </a>

        <div
          class="w-[38vw] px-[2vw] pb-[2vw] text-center font-din text-[2vw] leading-[1.1384] text-[#2a445d] md:px-0 xl:w-[58vh] xl:text-[3vh] 2xl:w-[29.5vw] 2xl:text-[1.5vw]"
        >
          Finally, a place to store all your business systems that’s super
          powerful, easy to use and insanely affordable. Try it out—free!
        </div>

        <img
          src="https://1brain-assets.s3.ap-southeast-2.amazonaws.com/actor.png"
          alt="login and register model"
          class="h-[10vh] w-full flex-1 object-cover object-top xl:object-contain xl:object-bottom"
        />
      </div>
    </div>
    <div class="flex min-h-screen flex-col">
      <div class="flex w-full flex-1 md:items-center md:justify-center md:py-5">
        <div
          class="flex w-full flex-col gap-6 md:mx-0 md:my-auto md:h-auto md:justify-center"
        >
          <div class="space-y-4 px-5 pb-6 pt-12 text-center md:pb-0 md:pt-4">
            <img
              :src="mobileLogo"
              class="mx-auto block w-full max-w-[265px] md:hidden"
              alt="logo"
            />
            <div
              class="m-auto max-w-[460px] space-y-2.5 md:mx-auto md:max-w-[460px]"
            >
              <h4
                class="text-2xl font-medium text-black dark:text-white md:text-center"
              >
                {{ props.subtitle }}
              </h4>
              <div
                class="text-sm font-normal text-black-600 dark:text-gray-400 md:text-base"
                v-if="props.description"
              >
                {{ props.description }}
              </div>
            </div>
          </div>

          <slot />
        </div>
      </div>

      <div
        class="flex flex-col items-center justify-center border-t md:flex-row"
      >
        <div>
          <ul
            class="flex flex-col flex-wrap justify-center gap-4 px-6 py-4 text-center xs:flex-row"
          >
            <li
              v-for="link of links"
              :key="link.label"
              class="md:text-md text-sm text-gray-700 hover:text-primary hover:underline dark:text-gray-300"
            >
              <a :href="link.href">{{ link.label }}</a>
            </li>
          </ul>
        </div>
        <div class="hidden">
          <ul
            class="flex flex-row flex-wrap justify-center gap-10 px-4 py-2 md:gap-4"
          >
            <li
              v-for="link of socials"
              :key="link.label"
              class="md:text-md text-sm text-gray-700 hover:text-primary hover:underline"
            >
              <a :href="link.href" v-html="link.icon"></a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  <ColorModeToggle class="fixed right-6 top-4 z-50 sm:right-4" />
  <Toast />
</template>

<style scoped>
.button-transition {
  transition:
    background-color 0.3s ease,
    border-color 0.3s ease,
    color 0.3s ease;
}
</style>
